// =============================================================================
// Unified platform detection — single source of truth.
//
// Every file that needs to know "what kind of client is this?" must import
// from here.  Ad-hoc `window.webkit?.messageHandlers` / `navigator.userAgent`
// checks elsewhere are prohibited.
// =============================================================================

export interface PlatformInfo {
  /** Maestro iOS app (WKWebView shell with __maestroNative injected). */
  isIOSNativeApp: boolean;
  /** iPhone/iPad running in Mobile Safari or Chrome (no native bridge). */
  isIOSMobileSafari: boolean;
  /** iOS home-screen PWA (standalone mode). */
  isIOSStandalone: boolean;
  /** Android device (native shell). */
  isAndroid: boolean;
  /** Tauri desktop app (macOS / Windows / Linux). */
  isTauriDesktop: boolean;
  /** Plain browser on a desktop/laptop (no Tauri). */
  isWebDesktop: boolean;

  /** True for any iPhone/iPad (native, Safari, or PWA). */
  isAnyIOS: boolean;
  /** True when a native bridge exists that can route UPnP/transport commands. */
  hasNativeBridge: boolean;
  /** True when the device runs local LAN discovery (Tauri or native app). */
  hasNativeDiscovery: boolean;

  /** Human-readable device label for BATON registration.  */
  deviceName: string;
  /** Short lowercase token for WebSocket URL / session prefix. */
  deviceType: string;
  /** Prefix for the persistent device ID stored in localStorage. */
  deviceIdPrefix: string;
}

let _cached: PlatformInfo | null = null;

export function detectPlatform(): PlatformInfo {
  if (_cached) return _cached;

  if (typeof window === 'undefined') {
    _cached = {
      isIOSNativeApp: false,
      isIOSMobileSafari: false,
      isIOSStandalone: false,
      isAndroid: false,
      isTauriDesktop: false,
      isWebDesktop: true,
      isAnyIOS: false,
      hasNativeBridge: false,
      hasNativeDiscovery: false,
      deviceName: 'Server',
      deviceType: 'unknown',
      deviceIdPrefix: 'srv',
    };
    return _cached;
  }

  const w = window as any;
  const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '';
  const uaLower = ua.toLowerCase();

  const hasIOSBridge = !!(w.__maestroNative?.platform === 'ios');
  const hasAndroidBridge = !!(w.__maestroNative?.platform === 'android');
  const hasTauri = !!w.__TAURI__;
  const isIPhoneUA = uaLower.includes('iphone');
  const isIPadUA = uaLower.includes('ipad');
  const isIOSUA = isIPhoneUA || isIPadUA;
  const isStandalone = !!(navigator as any).standalone;

  const isIOSNativeApp = hasIOSBridge;
  const isIOSStandalone = isIOSUA && !isIOSNativeApp && isStandalone;
  const isIOSMobileSafari = isIOSUA && !isIOSNativeApp && !isStandalone;
  const isAndroid = hasAndroidBridge;
  const isTauriDesktop = hasTauri && !hasIOSBridge && !hasAndroidBridge;
  const isAnyIOS = isIOSNativeApp || isIOSMobileSafari || isIOSStandalone;
  const isWebDesktop = !isAnyIOS && !isAndroid && !isTauriDesktop;

  const hasNativeBridge = isIOSNativeApp || isTauriDesktop;
  const hasNativeDiscovery = isIOSNativeApp || isTauriDesktop || isAndroid;

  let deviceName = 'Browser';
  if (isIOSNativeApp) {
    deviceName = isIPadUA ? 'iPad' : 'iPhone';
  } else if (isIOSMobileSafari) {
    deviceName = isIPadUA ? 'iPad (Safari)' : 'iPhone (Safari)';
  } else if (isIOSStandalone) {
    deviceName = isIPadUA ? 'iPad' : 'iPhone';
  } else if (isTauriDesktop) {
    deviceName = 'Desktop';
  } else if (!!w.__MAESTRO_CONNECTOR__) {
    deviceName = 'Desktop';
  } else if (/Mac OS X|Macintosh/i.test(ua)) {
    deviceName = 'Mac';
  }

  let deviceType = 'browser';
  if (isIOSNativeApp) deviceType = isIPadUA ? 'ipad' : 'iphone';
  else if (isIOSMobileSafari || isIOSStandalone) deviceType = isIPadUA ? 'ipad-safari' : 'iphone-safari';
  else if (isAndroid) deviceType = 'android';
  else if (isTauriDesktop) deviceType = 'desktop-connector';
  else if (uaLower.includes('mobile')) deviceType = 'mobile';

  let deviceIdPrefix = 'web';
  if (isIOSNativeApp) deviceIdPrefix = 'ios';
  else if (isIOSMobileSafari || isIOSStandalone) deviceIdPrefix = 'iosweb';
  else if (isAndroid) deviceIdPrefix = 'and';
  else if (isTauriDesktop) deviceIdPrefix = 'desk';

  _cached = {
    isIOSNativeApp,
    isIOSMobileSafari,
    isIOSStandalone,
    isAndroid,
    isTauriDesktop,
    isWebDesktop,
    isAnyIOS,
    hasNativeBridge,
    hasNativeDiscovery,
    deviceName,
    deviceType,
    deviceIdPrefix,
  };

  return _cached;
}

/**
 * Force re-evaluation (e.g. after the native bridge injects late).
 * Used by AppLayout once at startup after waiting for bridge readiness.
 */
export function resetPlatformCache(): void {
  _cached = null;
}

// =============================================================================
// Legacy API — delegates to detectPlatform() for backward compatibility.
// Existing imports continue to work without changing call sites in hooks/pages.
// =============================================================================

export type MaestroPlatform = 'tauri' | 'ios' | 'android' | 'web';

export function getPlatform(): MaestroPlatform {
  const p = detectPlatform();
  if (p.isIOSNativeApp) return 'ios';
  if (p.isAndroid) return 'android';
  if (p.isTauriDesktop) return 'tauri';
  return 'web';
}

export function isNativeApp(): boolean {
  const p = detectPlatform();
  return p.isIOSNativeApp || p.isTauriDesktop || p.isAndroid;
}

export function isIOSNativeApp(): boolean {
  return detectPlatform().isIOSNativeApp;
}

export function isTauriApp(): boolean {
  return detectPlatform().isTauriDesktop;
}

export function hasNativeDiscovery(): boolean {
  return detectPlatform().hasNativeDiscovery;
}

export function isDesktopMacOSUserAgent(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/(iPhone|iPad|iPod)/i.test(ua)) return false;
  return /Mac OS X|Macintosh/i.test(ua);
}
